import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import { useFormik } from "formik";
import React, { Fragment, useEffect } from "react";
import { useOrderDetails } from "../../contexts/orderDetails";
import { basePath } from "../config";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/auth";

const validate = (values, prefToEdit) => {
  const errors = {};

  if (!values.gap && prefToEdit?.type === "gap") {
    errors.gap = "Required";
  }

  if (!values.teeth && prefToEdit?.type === "teeth") {
    errors.teeth = "Required";
  }

  if (!values.paper_size && prefToEdit?.type === "paper_type") {
    errors.paper_size = "Required";
  }

  return errors;
};

async function doSubmit(
  values,
  action,
  activeUser,
  prefToEdit,
  orderDetails,
  formik,
  setOrderDetails,
  setPrefToEdit
) {
  try {
    const res = await fetch(basePath + "editOrderPref", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${activeUser?.remember_token}`,
      },
      body: JSON.stringify({
        ...values,
        type: prefToEdit?.type,
        id: orderDetails.id,
      }),
    });

    const data = await res.json();
    if (res.status === 401) {
      formik.resetForm({ values: values });
      document.cookie = "user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      toast.error(data.message);
      return 0;
    }

    if (data.status) {
      // console.log(data.status)
      setOrderDetails(JSON.parse(data.data));
      formik.resetForm();
      setPrefToEdit(null);
      toast.success(data.message);
    } else {
      // TODO:: handle form while API fails
      // setOrders(null)
      formik.resetForm({ values: values });
      toast.error("error: " + data.message);
      console.error("error: " + data.message);
    }
  } catch (error) {
    // TODO:: handle form while API fails
    toast.error("error: " + error);
    formik.resetForm({ values: values });
    console.error("srartOrder:: api calling fail =>", error);
  }
}

export default function EditOrderPreference({ prefToEdit, setPrefToEdit }) {
  const [
    { orderDetails, papersData, paperSizes },
    { setOrderDetails, setPapersData },
  ] = useOrderDetails();
  const [{ activeUser }] = useAuth();
  const isOpen = !!prefToEdit;
  function closeModal() {
    setPrefToEdit(null);
  }

  const formik = useFormik({
    initialValues: {
      gap: "",
      teeth: "",
      paper_size: "",
    },
    validate: (values) => validate(values, prefToEdit),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, action) => {
      doSubmit(
        values,
        action,
        activeUser,
        prefToEdit,
        orderDetails,
        formik,
        setOrderDetails,
        setPrefToEdit
      );
    },
  });

  useEffect(() => {
    const { gap, teeth, paper_size } = orderDetails;
    formik.setFieldValue("gap", gap);
    formik.setFieldValue("teeth", teeth);
    formik.setFieldValue("paper_size", paper_size);
  }, [prefToEdit]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  Edit{" "}
                  <span className="capitalize">
                    {prefToEdit?.type?.split("_").join(" ")}
                  </span>
                </Dialog.Title>

                <form onSubmit={formik.handleSubmit}>
                  <div className="grid mt-8">
                    {prefToEdit?.type === "gap" && (
                      <div>
                        <label className="block">
                          <span className="block text-sm font-medium text-slate-700 capitalize">
                            Gap
                          </span>
                          <input
                            type="text"
                            name="gap"
                            disabled={false}
                            onChange={formik.handleChange}
                            value={formik.values.gap}
                            className={classNames(
                              "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                              formik.errors?.gap && "border-red-600"
                            )}
                          />
                        </label>
                        {formik.errors.gap ? (
                          <span className="text-sm text-red-600">
                            {formik.errors?.gap}
                          </span>
                        ) : null}
                      </div>
                    )}

                    {prefToEdit?.type === "teeth" && (
                      <div>
                        <label className="block">
                          <span className="block text-sm font-medium text-slate-700 capitalize">
                            Teeth
                          </span>
                          <input
                            type="text"
                            name="teeth"
                            disabled={false}
                            onChange={formik.handleChange}
                            value={formik.values.teeth}
                            className={classNames(
                              "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                              formik.errors?.teeth && "border-red-600"
                            )}
                          />
                        </label>
                        {formik.errors.teeth ? (
                          <span className="text-sm text-red-600">
                            {formik.errors?.teeth}
                          </span>
                        ) : null}
                      </div>
                    )}

                    {prefToEdit?.type === "paper_size" && (
                      <div>
                        <label className="block">
                          <span className="block text-sm font-medium text-slate-700 capitalize">
                            paper size
                          </span>
                          <input
                            type="text"
                            name="paper_size"
                            disabled={false}
                            onChange={formik.handleChange}
                            value={formik.values.paper_size}
                            className={classNames(
                              "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                              formik.errors?.paper_size && "border-red-600"
                            )}
                          />
                        </label>
                        {formik.errors.paper_size ? (
                          <span className="text-sm text-red-600">
                            {formik.errors?.paper_size}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>

                  <div className="mt-8 flex justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formik.isSubmitting}
                      className={classNames(
                        "inline-flex justify-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2",
                        formik.isSubmitting &&
                          "disabled:bg-slate-300 disabled:text-gray-500"
                      )}
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
